import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import * as Social from '../../replicant/social';
import { user } from 'src/StateObserver';
import {
  maxSocialContinueRewardMoves,
  type SocialSubFeature,
} from 'src/replicant/social/social.ruleset';

import { AssetGroup } from '@play-co/timestep-core';

function getRewardCap(subFeature: SocialSubFeature) {
  if (subFeature === 'continue_social') {
    return maxSocialContinueRewardMoves;
  }

  return Infinity;
}

export default class PopupInviteReward extends PopupBasic {
  private rewardLabel: LangBitmapFontTextView;
  private reward: ImageSliceView;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: uiConfig.popups.minimumWidth,
      height: 410,
      tracking: { feature: 'invite', $subFeature: 'popup-invite-reward' },
    });

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 80,
      x: 40,
      y: 68,
      zIndex: 1,
    });

    this.box.addSubview(
      new ImageScaleView({
        height: 5,
        width: this.message.style.width,
        x: 40,
        y: 132,
        image: 'assets/ui/popups/panel_underline.png',
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 5, right: 5 },
        },
      }),
    );

    this.reward = this.box.addSubview(
      new ImageSliceView({
        image: 'assets/ui/shared/frames/inset_panel.png',
        slices: {
          left: 25,
          right: 25,
          top: 25,
          bot: 25,
        },
        centerOnOrigin: true,
        x: this.box.style.width / 2,
        y: 212,
        height: 88,
        width: 300,
      }),
    );

    this.rewardLabel = new LangBitmapFontTextView({
      size: 38,
      font: 'Body',
      height: 68,
      width: 136,
      verticalAlign: 'center',
      align: 'center',
      x: 120,
      y: 8,
    });

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.secondary,
        labelOffsetX: -4,
        labelOffsetY: -1,
        fontSize: 38,
        font: bitmapFonts('Body'),
        x: this.box.style.width / 2,
        y: this.box.style.height - 35 - 80 / 2,
        width: 229,
        height: 80,
        centerOnOrigin: true,
        localeText: () => i18n('basic.ok'),
        onClick: async () => {
          sounds.playSound('cancel');
          opts.close();
        },
        tracking: {
          feature: 'invite',
          $subFeature: 'popup-invite-reward-close',
        },
      }),
    );
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/popups/panel_underline.png',
      'assets/ui/shared/frames/inset_panel.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/hud/icons/hud_energy.png',
      'assets/ui/match2/common/moves.png',
    ]);
  }

  public init(opts: { inviteCount: number; subFeature: SocialSubFeature }) {
    super.init({
      title: i18n('social.invite.rewards.title'),
      message: i18n('social.invite.rewards.subtitle'),
    });

    // Detect reward type depend on sub feature
    const reward = Social.getters.getInviteReward(user(), opts.subFeature);

    // Update reward type icon
    this.reward.removeAllSubviews();
    this.reward.addSubview(this.rewardLabel);
    this.reward.addSubview(this.getRewardIcon(reward.type));
    const rewardValue = Math.min(
      getRewardCap(opts.subFeature),
      reward.value * opts.inviteCount,
    );

    // Set reward value
    this.rewardLabel.localeText = () => `x ${parseAmount(rewardValue)}`;
  }

  /**
   * Get reward icon
   */
  private getRewardIcon(type: 'gems' | 'energy' | 'eventEnergy' | 'moves') {
    switch (type) {
      case 'gems':
        return new ImageScaleView({
          scaleMethod: 'contain',
          image: 'assets/ui/hud/icons/hud_gem.png',
          width: 74,
          height: 74,
          x: 60,
          y: 7,
        });
      case 'energy':
        return new ImageScaleView({
          scaleMethod: 'contain',
          image: 'assets/ui/hud/icons/hud_energy.png',
          width: 74,
          height: 74,
          x: 60,
          y: 7,
        });
      case 'eventEnergy':
        return new ImageScaleView({
          scaleMethod: 'contain',
          image: 'assets/ui/hud/icons/hud_energy.png',
          width: 74,
          height: 74,
          x: 60,
          y: 7,
        });
      case 'moves':
        return new ImageScaleView({
          scaleMethod: 'contain',
          image: 'assets/ui/match2/common/moves.png',
          width: 74,
          height: 74,
          x: 60,
          y: 7,
        });
    }
  }
}
